import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Subscription} from "rxjs";
import * as _ from "lodash";
import {PlanningDraftJobChangesFacadeService} from "../../services/planning-draft-job-changes-facade.service";
import {PlanningDraftJobChangesHttpService} from "../../services/planning-draft-job-changes-http.service";
import {DraftJobChanges} from "../../../models/draft-job-changes.model";
import {DraftJobChangesFilter} from "../../../models/draft-job-changes-filter.model";
import {DraftJobChangeType} from "../../../types/draft-job-change-type.enum";
import {DraftJobChangeStatus} from "../../../types/draft-job-change-status.enum";
import {DraftJobChangeReason} from "../../../types/draft-job-change-reason.enum";

const PAGE_SIZE = 50;

// Every type except BLOCKED — used to hide unavailable/informational-only changes by default,
// since the API has no direct "availability"/"exclude type" filter, only an include-set of types.
const AVAILABLE_CHANGE_TYPES = Object.values(DraftJobChangeType).filter((type) => type !== DraftJobChangeType.BLOCKED);

require('./planning-draft-job-changes-list.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-job-changes-list',
  templateUrl: './planning-draft-job-changes-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftJobChangesListComponent implements OnInit, OnDestroy {
  @Input() projectId: string;
  @Input() draftId: string;

  public changes: DraftJobChanges[] = [];
  public totalElements = 0;
  public unavailableCount = 0;
  public page = 0;
  public size = PAGE_SIZE;
  public selectedIds: string[] = [];
  public selectableIds: string[] = [];
  public selectAllMatching = false;
  public loading = false;
  public expandedId: string = null;
  public filters: DraftJobChangesFilter = {};
  public showUnavailable = true;
  public query = '';

  public readonly changeTypeOptions = Object.values(DraftJobChangeType);
  public readonly statusOptions = Object.values(DraftJobChangeStatus);
  public readonly reasonOptions = Object.values(DraftJobChangeReason);

  private subscriptions: Subscription[] = [];

  constructor(@Inject(PlanningDraftJobChangesFacadeService) private facade: PlanningDraftJobChangesFacadeService,
              @Inject(PlanningDraftJobChangesHttpService) private httpService: PlanningDraftJobChangesHttpService,
              @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.subscriptions.push(
      this.facade.selectAll().subscribe((changes) => {
        this.changes = changes;
        this.cdr.markForCheck();
      }),
      this.facade.selectTotalElements().subscribe((totalElements) => {
        this.totalElements = totalElements;
        this.cdr.markForCheck();
      }),
      this.facade.selectPage().subscribe((page) => {
        this.page = page;
        this.cdr.markForCheck();
      }),
      this.facade.selectSelectedIds().subscribe((selectedIds) => {
        this.selectedIds = selectedIds;
        this.cdr.markForCheck();
      }),
      this.facade.selectSelectAllMatching().subscribe((selectAllMatching) => {
        this.selectAllMatching = selectAllMatching;
        this.cdr.markForCheck();
      }),
      this.facade.selectSelectableIds().subscribe((selectableIds) => {
        this.selectableIds = selectableIds;
        this.cdr.markForCheck();
      }),
      this.facade.selectLoading().subscribe((loading) => {
        this.loading = loading;
        this.cdr.markForCheck();
      })
    );

    this.fetchPage(0);
    this.fetchUnavailableCount();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onFiltersChange(): void {
    // fetchPage below dispatches setFilters(effectiveFilters()) itself — effectiveFilters() already
    // builds a fresh object via _.omitBy, not the live `filters` reference, so NgRx's dev-mode
    // immutability checks won't deep-freeze this component's own `filters` and break ng-select's
    // next in-place `filters.statuses = ...`/etc. write (two-way [(ngModel)] binding).
    this.fetchPage(0, true);
    this.fetchUnavailableCount();
  }

  // routeIds/accountIds/orderExternalIds have no ready-made multi-select picker wired up yet
  // (they'd need the draft's routes/accounts/orders lists passed in) — comma-separated free
  // text for now.
  onIdsFilterChange(key: 'routeIds' | 'accountIds' | 'orderExternalIds', rawValue: string): void {
    this.filters = {...this.filters, [key]: _.compact((rawValue || '').split(',').map((value) => value.trim()))};
    this.onFiltersChange();
  }

  onToggleShowUnavailable(showUnavailable: boolean): void {
    this.showUnavailable = showUnavailable;
    this.fetchPage(0, true);
  }

  // The jobChanges/page endpoint doesn't support free-text search yet — `query` is sent as a
  // forward-looking param (see DraftJobChangesPageParams) so this is ready the moment the backend
  // adds support. Until then, expect the server to ignore it and the list to not actually narrow.
  onQueryChange(query: string): void {
    this.query = query || '';
    this.fetchPage(0, true);
  }

  onPaginationChange(pageIndex: number): void {
    this.fetchPage(pageIndex - 1);
  }

  onToggleExpand(change: DraftJobChanges): void {
    this.expandedId = this.expandedId === change.id ? null : change.id;
  }

  onToggleSelect(change: DraftJobChanges): void {
    const isSelected = this.selectedIds.includes(change.id);
    const selectedIds = isSelected
      ? this.selectedIds.filter((id) => id !== change.id)
      : [...this.selectedIds, change.id];
    this.facade.setSelectedIds(selectedIds);
  }

  // One click: when there are more matching changes than fit on this page, "select all" means
  // every matching change across every page (selectAllMatching), not just the loaded rows — the
  // apply/dismiss endpoints treat a filter with no `ids` as "everything matching", server-side.
  // Clicking again (isAllSelected already true, either way) clears the selection.
  onToggleSelectAll(): void {
    if (this.isAllSelected) {
      this.onClearSelection();
      return;
    }
    if (this.totalElements > this.selectableIds.length) {
      this.facade.setSelectAllMatching(true);
    } else {
      this.facade.setSelectedIds([...this.selectableIds]);
    }
  }

  onClearSelection(): void {
    this.facade.setSelectedIds([]);
    this.facade.setSelectAllMatching(false);
  }

  get isAllSelected(): boolean {
    return this.selectAllMatching ||
      (!!this.selectableIds.length && this.selectableIds.every((id) => this.selectedIds.includes(id)));
  }

  get isIndeterminate(): boolean {
    return !this.selectAllMatching && !this.isAllSelected && this.selectedIds.some((id) => this.selectableIds.includes(id));
  }

  // `isNewQuery` marks a fetch caused by a filter/search/toggle change (as opposed to plain
  // pagination) — only those invalidate an active "select all matching" choice, since the set it
  // refers to just changed.
  private fetchPage(page: number, isNewQuery = false): void {
    if (isNewQuery) {
      this.facade.setSelectAllMatching(false);
    }
    const effectiveFilters = this.effectiveFilters();
    this.facade.setFilters(effectiveFilters);
    const params: any = {...effectiveFilters, page, size: this.size};
    if (this.query) {
      params.query = this.query;
    }
    this.facade.getPage(this.projectId, this.draftId, params);
  }

  // A lightweight, size:1 request just to read the BLOCKED-only totalElements — the summary
  // banner's "N Unavailable Updates" count, independent of whatever the main list is filtered to.
  private fetchUnavailableCount(): void {
    const cleanFilters = _.omitBy(this.filters, (value) => !value || !value.length);
    this.httpService.getPage(this.projectId, this.draftId, {
      ...cleanFilters,
      changeTypes: [DraftJobChangeType.BLOCKED],
      page: 0,
      size: 1
    }).subscribe({
      next: (res) => {
        this.unavailableCount = res.totalElements;
        this.cdr.markForCheck();
      },
      error: () => {
      }
    });
  }

  private effectiveFilters(): DraftJobChangesFilter {
    const cleanFilters = _.omitBy(this.filters, (value) => !value || !value.length) as DraftJobChangesFilter;
    if (!this.showUnavailable && !cleanFilters.changeTypes?.length) {
      return {...cleanFilters, changeTypes: AVAILABLE_CHANGE_TYPES};
    }
    return cleanFilters;
  }
}
